import { BRAND } from "themes";

const mediaFrame = {
  position: "relative" as const,
  width: "100%",
  aspectRatio: "16 / 9",
  minHeight: 160,
  background: "#0f172a",
  overflow: "hidden",
  flexShrink: 0,
  lineHeight: 0,
};

const mediaFit = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "contain" as const,
  objectPosition: "center",
  display: "block",
  background: "#0f172a",
};

export const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "14px",
    padding: "16px",
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 180,
    gridColumn: "1 / -1",
    color: BRAND.muted,
    fontSize: "0.95rem",
  },
  card: {
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "12px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0 10px 24px rgba(27, 19, 64, 0.08)",
      transform: "translateY(-2px)",
    },
  },
  mediaFrame,
  mediaFit,
  thumb: mediaFrame,
  thumbMedia: mediaFit,
  thumbnailWrapper: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    cursor: "pointer",
    overflow: "hidden",
  },
  mediaPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.muted,
    fontSize: "0.9rem",
    background: "#0f172a",
  },
  playOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(17, 24, 39, 0.25)",
    transition: "background 0.2s ease",
    ".thumbnail-wrapper:hover > &": {
      background: "rgba(17, 24, 39, 0.4)",
    },
  },
  playBtn: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.92)",
    color: BRAND.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 30,
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
    transition: "transform 0.2s ease, background 0.2s ease",
    ".thumbnail-wrapper:hover &": {
      transform: "scale(1.08)",
      background: "#fff",
    },
  },
  cardBody: {
    flex: 1,
    padding: "14px",
  },
  cardTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.35,
    color: BRAND.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardFooter: {
    display: "flex",
    borderTop: `1px solid ${BRAND.border}`,
  },
  cardAction: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    padding: "9px 0",
    border: "none",
    background: "transparent",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    textTransform: "none",
    borderRadius: 0,
  },
  editAction: {
    color: BRAND.primary,
    borderRight: `1px solid ${BRAND.border}`,
    "&:hover": { background: "#F3EEFF" },
  },
  deleteAction: {
    color: "#ef4444",
    "&:hover": { background: "#fef2f2" },
  },
  listPaper: {
    overflow: "hidden",
    marginTop: 0,
  },
  pagination: {
    padding: "8px 16px",
    borderTop: "none",
    backgroundColor: "#fff",
    "& .MuiToolbar-root.MuiTablePagination-toolbar": {
      padding: 0,
      minHeight: "52px",
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      fontSize: "0.8125rem",
    },
    "& .MuiTablePagination-root": {
      borderTop: "none",
      backgroundColor: "transparent",
    },
  },
  formGroup: {
    marginBottom: "20px",
    "&:last-of-type": {
      marginBottom: 0,
    },
  },
  fieldLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: "8px",
  },
  requiredStar: {
    color: "#ef4444",
    marginLeft: "2px",
  },
  selectInput: {
    borderRadius: "8px",
    fontSize: 14,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d1d5db",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d1d5db",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BRAND.primary,
    },
    "& .MuiSelect-select": {
      padding: "10px 14px",
    },
  },
  dropzone: {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    border: "2px dashed #cbd5e1",
    borderRadius: "12px",
    background: "#f8fafc",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: "10px",
    cursor: "pointer",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease, background 0.2s ease",
    "&:hover": {
      borderColor: "#C9B6F5",
      background: "#F8F6FC",
    },
  },
  dropzoneDragging: {
    borderColor: BRAND.primary,
    background: "#F3EEFF",
  },
  dropzoneIcon: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: "#F3EEFF",
    color: BRAND.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
  },
  dropzoneTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1f2937",
  },
  dropzoneSub: {
    fontSize: 12,
    color: "#6b7280",
  },
  dropzoneBrowse: {
    color: BRAND.primary,
    fontWeight: 600,
  },
  previewCard: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    overflow: "hidden",
    background: "#fff",
  },
  previewImageWrap: mediaFrame,
  previewVideoWrap: mediaFrame,
  previewPlayer: mediaFit,
  thumbnailPreviewImage: mediaFit,
  previewMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "12px 14px",
  },
  previewInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
  },
  previewFileIcon: {
    width: 36,
    height: 36,
    borderRadius: "8px",
    background: "#F3EEFF",
    color: BRAND.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  previewText: {
    minWidth: 0,
  },
  previewName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: { xs: 160, sm: 260 },
  },
  previewSize: {
    fontSize: 12,
    color: "#6b7280",
  },
  previewRemove: {
    background: "#fef2f2",
    color: "#ef4444",
    width: 30,
    height: 30,
    borderRadius: "8px",
    flexShrink: 0,
    "&:hover": { background: "#fee2e2" },
  },
  thumbMediaCover: mediaFit,
};
